import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, Star, Store as StoreIcon, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFilters } from '@/state/filters';
import { useStoresList, type StoreLite } from '@/api/legacy/stores';
import { storage } from '@/lib/storage';
import { useSettingsStore } from '@/api/bi/settings';
import { cn } from '@/lib/cn';

/**
 * Multi-store picker.
 *
 * Selection model:
 *   - Empty selection = "All stores" (no filter).
 *   - Otherwise = explicit list of store ids; the BI plan is moving toward
 *     real multi-store fan-out so consumers should read `storeIds`, but
 *     legacy single-store call sites keep working through the
 *     `storeIds[0]` shim in `useFilters`.
 *
 * UX (Stage-1 multi-select):
 *   - Every row is a checkbox-style toggle (no ⌘ modifier required).
 *   - Header rail offers "Select all visible" + "Deselect all" so an
 *     operator can scope to a region quickly using the search input.
 *   - Star pins/unpins favourites (mirrored to the server settings blob).
 *
 * Animation notes (Emil):
 *   - Origin-aware popover via `--radix-popover-content-transform-origin`.
 *   - Color-only hover; checkbox tick uses `.press` for tactile feedback.
 */
export function StorePicker() {
  const storeIds = useFilters((s) => s.storeIds);
  const setStoreIds = useFilters((s) => s.setStoreIds);
  const toggleStoreId = useFilters((s) => s.toggleStoreId);
  const clearStores = useFilters((s) => s.clearStores);
  const { data: stores = [], isLoading } = useStoresList();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [favs, setFavs] = useState<number[]>(() => storage.getFavStores());
  const [recents, setRecents] = useState<number[]>(() => storage.getRecentStores());
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(storeIds), [storeIds]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIdx(0);
    setFavs(storage.getFavStores());
    setRecents(storage.getRecentStores());
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filter = (s: StoreLite) => !needle || s.name.toLowerCase().includes(needle);
    const favList = stores.filter((s) => favs.includes(s.id) && filter(s));
    const recentList = stores
      .filter((s) => !favs.includes(s.id) && recents.includes(s.id) && filter(s))
      .sort((a, b) => recents.indexOf(a.id) - recents.indexOf(b.id));
    const others = stores
      .filter((s) => !favs.includes(s.id) && !recents.includes(s.id) && filter(s))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { favList, recentList, others };
  }, [stores, favs, recents, query]);

  // Flat row index drives the keyboard cursor. Order matches render
  // order so ↑/↓ feels predictable.
  const flatRows = useMemo(() => {
    const all: StoreLite[] = [];
    grouped.favList.forEach((s) => all.push(s));
    grouped.recentList.forEach((s) => all.push(s));
    grouped.others.forEach((s) => all.push(s));
    return all;
  }, [grouped]);

  // The set of ids currently *visible* (after search) — used by
  // "Select all visible" so we don't blow away off-screen selections.
  const visibleIds = useMemo(
    () => flatRows.map((s) => s.id),
    [flatRows],
  );

  useEffect(() => {
    if (activeIdx >= flatRows.length) setActiveIdx(Math.max(0, flatRows.length - 1));
  }, [activeIdx, flatRows.length]);

  function toggleFav(id: number) {
    const next = favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id];
    setFavs(next);
    storage.setFavStores(next);
    try {
      useSettingsStore.getState().patchLocal({ fav_stores: next });
    } catch {
      /* noop — settings store may not be ready (logged-out boot, tests). */
    }
  }

  function selectAllVisible() {
    if (visibleIds.length === 0) return;
    const next = new Set(storeIds);
    for (const id of visibleIds) next.add(id);
    setStoreIds(Array.from(next));
  }

  function deselectAllVisible() {
    if (visibleIds.length === 0) return;
    const visibleSet = new Set(visibleIds);
    setStoreIds(storeIds.filter((id) => !visibleSet.has(id)));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flatRows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const row = flatRows[activeIdx];
      if (row) toggleStoreId(row.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      selectAllVisible();
    }
  }

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const triggerLabel = isLoading
    ? 'Loading stores…'
    : storeIds.length === 0
      ? 'All stores'
      : storeIds.length === 1
        ? stores.find((s) => s.id === storeIds[0])?.name ?? `Store #${storeIds[0]}`
        : `${storeIds.length} stores`;

  // Online dot for the trigger. For multi we show the aggregate state.
  const triggerOnline = useMemo(() => {
    if (storeIds.length === 0) return null;
    if (storeIds.length === 1) {
      const s = stores.find((x) => x.id === storeIds[0]);
      if (!s) return null;
      return s.is_online ? 'on' : 'off';
    }
    const onlineCount = stores.filter((s) => selectedSet.has(s.id) && s.is_online).length;
    return onlineCount > 0 ? 'on' : 'off';
  }, [stores, storeIds, selectedSet]);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={isLoading}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'press inline-flex h-8 min-w-[140px] max-w-[220px] items-center justify-between gap-2 rounded-md border border-input bg-surface px-2.5 text-xs focus-ring sm:min-w-[180px]',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <StoreIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate">{triggerLabel}</span>
            {triggerOnline === 'on' ? (
              <span className="relative inline-flex h-2 w-2 shrink-0">
                <span className="absolute inset-0 animate-[fasto-pulse_1800ms_ease-out_infinite] rounded-full bg-success/70" />
                <span className="relative inline-block h-2 w-2 rounded-full bg-success" />
              </span>
            ) : triggerOnline === 'off' ? (
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40"
                aria-label="all selected stores offline"
              />
            ) : null}
          </span>
          {storeIds.length > 0 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearStores();
              }}
              className="press rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="Clear store filter"
              tabIndex={-1}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'pop-anim z-[1000] w-[300px] overflow-hidden rounded-md border border-border bg-surface-elevated text-foreground shadow-soft',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-150 ease-out',
          )}
        >
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search stores…"
              className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-2 py-1 text-[10px] text-muted-foreground">
            <span>
              {storeIds.length === 0 ? (
                'All stores'
              ) : (
                <>
                  <span className="font-medium text-foreground">{storeIds.length}</span> selected
                </>
              )}
            </span>
            <span className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={selectAllVisible}
                disabled={visibleIds.length === 0}
                className="press rounded-sm px-1.5 py-0.5 hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                Select all visible
              </button>
              <button
                type="button"
                onClick={query ? deselectAllVisible : clearStores}
                disabled={storeIds.length === 0}
                className="press rounded-sm px-1.5 py-0.5 hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                {query ? 'Deselect visible' : 'Clear'}
              </button>
            </span>
          </div>

          {storeIds.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/30 px-2 py-1.5">
              {storeIds.slice(0, 6).map((id) => {
                const s = stores.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full bg-surface-elevated px-1.5 py-0.5 text-[10px] ring-1 ring-inset ring-border"
                  >
                    {s?.is_online ? (
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
                    ) : (
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                    )}
                    <span className="max-w-[88px] truncate">{s?.name ?? `Store #${id}`}</span>
                    <button
                      type="button"
                      onClick={() => toggleStoreId(id)}
                      className="press rounded-full p-0.5 text-muted-foreground hover:text-danger"
                      aria-label={`Remove ${s?.name ?? id}`}
                    >
                      <X className="h-2.5 w-2.5" aria-hidden />
                    </button>
                  </span>
                );
              })}
              {storeIds.length > 6 ? (
                <span className="text-[10px] text-muted-foreground">+{storeIds.length - 6} more</span>
              ) : null}
            </div>
          ) : null}

          <div ref={listRef} className="max-h-[320px] overflow-y-auto p-1" role="listbox" aria-multiselectable>
            {grouped.favList.length > 0 ? (
              <SectionHeader label="Favorites" icon={<Star className="h-3 w-3" aria-hidden />} />
            ) : null}
            {grouped.favList.map((s, i) => {
              const idx = i;
              return (
                <StoreRow
                  key={s.id}
                  store={s}
                  idx={idx}
                  selected={selectedSet.has(s.id)}
                  active={activeIdx === idx}
                  isFav
                  onToggle={() => toggleStoreId(s.id)}
                  onToggleFav={() => toggleFav(s.id)}
                  onMouseEnter={() => setActiveIdx(idx)}
                />
              );
            })}

            {grouped.recentList.length > 0 ? <SectionHeader label="Recent" /> : null}
            {grouped.recentList.map((s, i) => {
              const idx = grouped.favList.length + i;
              return (
                <StoreRow
                  key={s.id}
                  store={s}
                  idx={idx}
                  selected={selectedSet.has(s.id)}
                  active={activeIdx === idx}
                  onToggle={() => toggleStoreId(s.id)}
                  onToggleFav={() => toggleFav(s.id)}
                  onMouseEnter={() => setActiveIdx(idx)}
                />
              );
            })}

            {grouped.others.length > 0 ? (
              <SectionHeader
                label={grouped.favList.length || grouped.recentList.length ? 'All stores' : ''}
              />
            ) : null}
            {grouped.others.map((s, i) => {
              const idx = grouped.favList.length + grouped.recentList.length + i;
              return (
                <StoreRow
                  key={s.id}
                  store={s}
                  idx={idx}
                  selected={selectedSet.has(s.id)}
                  active={activeIdx === idx}
                  onToggle={() => toggleStoreId(s.id)}
                  onToggleFav={() => toggleFav(s.id)}
                  onMouseEnter={() => setActiveIdx(idx)}
                />
              );
            })}

            {!isLoading && flatRows.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No stores match <span className="font-medium text-foreground">"{query}"</span>.
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-1.5 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Star className="h-3 w-3" aria-hidden /> Star to pin · Space to toggle
            </span>
            <span className="font-mono">↑↓ · ↵ · Esc</span>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function SectionHeader({ label, icon }: { label: string; icon?: React.ReactNode }) {
  if (!label) return <div className="my-1 border-t border-border/60" />;
  return (
    <div className="mt-1 flex items-center gap-1.5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
      {icon}
      {label}
    </div>
  );
}

interface StoreRowProps {
  store: StoreLite;
  idx: number;
  selected: boolean;
  active: boolean;
  isFav?: boolean;
  onToggle: () => void;
  onToggleFav: () => void;
  onMouseEnter: () => void;
}

function StoreRow({
  store,
  idx,
  selected,
  active,
  isFav,
  onToggle,
  onToggleFav,
  onMouseEnter,
}: StoreRowProps) {
  return (
    <div
      role="option"
      aria-selected={selected}
      data-idx={idx}
      onMouseEnter={onMouseEnter}
      onClick={onToggle}
      className={cn(
        'group flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors duration-100 ease-out',
        active && 'bg-muted',
        selected && 'text-foreground',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border transition-colors',
          selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input bg-surface',
        )}
      >
        {selected ? <Check className="h-2.5 w-2.5" aria-hidden /> : null}
      </span>
      <span className="relative inline-flex h-2 w-2 shrink-0">
        {store.is_online ? (
          <>
            <span className="absolute inset-0 animate-[fasto-pulse_1800ms_ease-out_infinite] rounded-full bg-success/60" />
            <span className="relative inline-block h-2 w-2 rounded-full bg-success" />
          </>
        ) : (
          <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/40" />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate">{store.name}</span>
      {!store.is_online ? (
        <span className="text-[10px] text-muted-foreground">offline</span>
      ) : null}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleFav();
        }}
        className={cn(
          'press ml-1 rounded p-0.5 text-muted-foreground/60 hover:text-foreground focus-ring',
          isFav && 'text-warning',
        )}
        aria-label={isFav ? 'Unpin store' : 'Pin store'}
        tabIndex={-1}
      >
        <Star className={cn('h-3.5 w-3.5', isFav && 'fill-warning')} aria-hidden />
      </button>
    </div>
  );
}
